import React, { useMemo } from 'react';
import type { EChartsOption } from 'echarts';
import ChartCard from '@/components/ChartCard';
import { format2 } from '@/utils/charts';
import type { AGCStorageDetailRow, AgcStorageReviewStrategy, AgcStorageTheoreticalOptimalRow } from '@/types/api';

interface SingleChartProps {
  rows: AGCStorageDetailRow[];
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  height?: number;
  title?: string;
}

interface StorageDetailChartsProps {
  rows: AGCStorageDetailRow[];
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
}

interface StrategyCompareChartProps {
  actualRows: AGCStorageDetailRow[];
  theoreticalRows: AgcStorageTheoreticalOptimalRow[];
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  height?: number;
  title?: string;
}

interface MultiPerspectiveReviewChartProps {
  actualRows: AGCStorageDetailRow[];
  theoreticalRows: AgcStorageTheoreticalOptimalRow[];
  reviewStrategies: AgcStorageReviewStrategy[];
  targetDate?: string;
  windowStart?: string;
  windowEnd?: string;
  loading?: boolean;
  error?: Error | null;
  onRetry?: () => void;
  height?: number;
  title?: string;
}

function slotFromTimeLabel(time: string): number | null {
  const [hourText, minuteText] = String(time).split(':');
  const hour = Number(hourText);
  const minute = Number(minuteText);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return null;
  return hour * 4 + Math.floor(minute / 15);
}

function useStrategyCompareOption(
  actualRows: AGCStorageDetailRow[],
  theoreticalRows: AgcStorageTheoreticalOptimalRow[]
): EChartsOption {
  return useMemo(() => {
    if (!theoreticalRows.length && !actualRows.length) return {};

    const actualBySlot = new Map<number, { bat: number; ep: number; p: number; count: number }>();
    for (const row of actualRows) {
      const slot = slotFromTimeLabel(row.time);
      if (slot === null) continue;
      const prev = actualBySlot.get(slot) ?? { bat: 0, ep: 0, p: 0, count: 0 };
      prev.bat += Number(row.bat) || 0;
      prev.ep += Number(row.ep) || 0;
      prev.p += Number(row.p) || 0;
      prev.count += 1;
      actualBySlot.set(slot, prev);
    }

    const theoreticalBySlot = new Map(theoreticalRows.map((row) => [row.slot, row]));
    const allSlots = Array.from(
      new Set([
        ...Array.from(actualBySlot.keys()),
        ...Array.from(theoreticalBySlot.keys()),
      ])
    ).sort((a, b) => a - b);

    const labels = allSlots.map((slot) => theoreticalBySlot.get(slot)?.period ?? `${Math.floor(slot / 4).toString().padStart(2, '0')}:${((slot % 4) * 15).toString().padStart(2, '0')}`);
    const actualPower = allSlots.map((slot) => {
      const row = actualBySlot.get(slot);
      return row && row.count ? row.bat / row.count : 0;
    });
    const declaredPower = allSlots.map((slot) => {
      const row = actualBySlot.get(slot);
      return row && row.count ? row.ep / row.count : 0;
    });
    const theoreticalPower = allSlots.map((slot) => {
      const row = theoreticalBySlot.get(slot);
      return row ? (Number(row.discharge_power) || 0) - (Number(row.charge_power) || 0) : 0;
    });
    const price = allSlots.map((slot) => {
      const theoretical = theoreticalBySlot.get(slot);
      if (theoretical) return Number(theoretical.price) || 0;
      const row = actualBySlot.get(slot);
      return row && row.count ? row.p / row.count : 0;
    });

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        valueFormatter: (value: any) => typeof value === 'number' ? format2(value) : value,
      },
      toolbox: { show: false },
      dataZoom: { show: false },
      legend: {
        data: ['实际充放电', '申报充放电', '理论充放电', '实时电价(元/MWh)'],
      },
      grid: { left: 60, right: 80, top: 44, bottom: 64 },
      xAxis: {
        type: 'category',
        data: labels,
        axisLabel: { rotate: 30, fontSize: 10 },
      },
      yAxis: [
        {
          type: 'value',
          name: '功率 (MW)',
          position: 'left',
        },
        {
          type: 'value',
          name: '电价 (元/MWh)',
          position: 'right',
          axisLine: { show: true },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: '实际充放电',
          type: 'bar',
          data: actualPower,
          itemStyle: { color: 'rgba(114,46,209,0.36)' },
          yAxisIndex: 0,
        },
        {
          name: '申报充放电',
          type: 'line',
          data: declaredPower,
          lineStyle: { color: '#f5222d', width: 2.2 },
          itemStyle: { color: '#f5222d' },
          showSymbol: false,
          yAxisIndex: 0,
        },
        {
          name: '理论充放电',
          type: 'line',
          data: theoreticalPower,
          lineStyle: { color: '#1677ff', width: 2.5 },
          itemStyle: { color: '#1677ff' },
          showSymbol: false,
          yAxisIndex: 0,
        },
        {
          name: '实时电价(元/MWh)',
          type: 'line',
          data: price,
          lineStyle: { color: '#52c41a', type: 'dashed' as const },
          itemStyle: { color: '#52c41a' },
          showSymbol: false,
          yAxisIndex: 1,
        },
      ],
    };
  }, [actualRows, theoreticalRows]);
}

function toFiniteNumber(value: unknown, fallback = 0): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function timestampKey(targetDate: string | undefined, time: string): string {
  if (targetDate && /^\d{2}:\d{2}$/.test(String(time))) {
    return `${targetDate}T${time}:00`;
  }
  return String(time);
}

function labelFromKey(key: string): string {
  const match = key.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}:\d{2})/);
  if (match) return `${match[2]}-${match[3]} ${match[4]}`;
  return key;
}

function sortTimeKeys(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const at = Date.parse(a);
    const bt = Date.parse(b);
    if (Number.isFinite(at) && Number.isFinite(bt)) return at - bt;
    return a.localeCompare(b);
  });
}

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000;
const DEFAULT_ACTUAL_INTERVAL_HOURS = 5 / 60;
const DEFAULT_STRATEGY_INTERVAL_HOURS = 15 / 60;

interface PowerBucket {
  energyMwh: number;
  hours: number;
}

interface PriceBucket {
  value: number;
  count: number;
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function canonicalTimeKey(timeMs: number): string {
  const date = new Date(timeMs);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}T${pad2(date.getHours())}:${pad2(date.getMinutes())}:00`;
}

function parsedTimeMs(key: string): number | null {
  const time = Date.parse(key);
  return Number.isFinite(time) ? time : null;
}

function floorToFifteenMinuteKey(key: string): string {
  const time = parsedTimeMs(key);
  if (time === null) return key;
  const date = new Date(time);
  date.setMinutes(Math.floor(date.getMinutes() / 15) * 15, 0, 0);
  return canonicalTimeKey(date.getTime());
}

function buildFifteenMinuteAxis(keys: string[], windowStart?: string, windowEnd?: string): string[] {
  const windowStartMs = windowStart ? parsedTimeMs(windowStart) : null;
  const windowEndMs = windowEnd ? parsedTimeMs(windowEnd) : null;
  if (windowStartMs !== null && windowEndMs !== null && windowEndMs > windowStartMs) {
    const axis: string[] = [];
    for (let cursor = windowStartMs; cursor < windowEndMs; cursor += FIFTEEN_MINUTES_MS) {
      axis.push(canonicalTimeKey(cursor));
    }
    return axis;
  }

  const ordered = sortTimeKeys(keys);
  const times = ordered.map(parsedTimeMs).filter((value): value is number => value !== null);
  if (!times.length || times.length !== ordered.length) return ordered;

  const start = Math.min(...times);
  const end = Math.max(...times);
  if (end < start || end - start > 3 * 24 * 60 * 60 * 1000) return ordered;

  const axis: string[] = [];
  for (let cursor = start; cursor <= end; cursor += FIFTEEN_MINUTES_MS) {
    axis.push(canonicalTimeKey(cursor));
  }
  return axis;
}

function addPowerSample(
  map: Map<string, PowerBucket>,
  key: string,
  powerMw: number,
  intervalHours: number,
) {
  const bucket = map.get(key) ?? { energyMwh: 0, hours: 0 };
  bucket.energyMwh += powerMw * intervalHours;
  bucket.hours += intervalHours;
  map.set(key, bucket);
}

function latestStrategy(
  strategies: AgcStorageReviewStrategy[],
  type: AgcStorageReviewStrategy['strategy_type']
): AgcStorageReviewStrategy | undefined {
  const matched = strategies.filter((strategy) => strategy.strategy_type === type);
  return matched[matched.length - 1];
}

function addPriceSample(
  map: Map<string, PriceBucket>,
  key: string,
  value: number,
) {
  const bucket = map.get(key) ?? { value: 0, count: 0 };
  bucket.value += value;
  bucket.count += 1;
  map.set(key, bucket);
}

function addLabel(
  labels: Map<string, string>,
  key: string,
  label: string,
) {
  labels.set(key, labels.get(key) ?? label);
}

function readPowerSeries(map: Map<string, PowerBucket>, orderedKeys: string[]): number[] {
  return orderedKeys.map((key) => {
    const bucket = map.get(key);
    if (!bucket || bucket.hours <= 0) return 0;
    return bucket.energyMwh / bucket.hours;
  });
}

function readPriceSeries(map: Map<string, PriceBucket>, orderedKeys: string[]): Array<number | null> {
  return orderedKeys.map((key) => {
    const bucket = map.get(key);
    if (!bucket || bucket.count <= 0) return null;
    return bucket.value / bucket.count;
  });
}

function useMultiPerspectiveReviewOption(
  actualRows: AGCStorageDetailRow[],
  theoreticalRows: AgcStorageTheoreticalOptimalRow[],
  reviewStrategies: AgcStorageReviewStrategy[],
  targetDate?: string,
  windowStart?: string,
  windowEnd?: string,
): EChartsOption {
  return useMemo(() => {
    if (!actualRows.length && !theoreticalRows.length && !reviewStrategies.length) return {};

    const windowStartMs = windowStart ? parsedTimeMs(windowStart) : null;
    const windowEndMs = windowEnd ? parsedTimeMs(windowEnd) : null;
    const inWindow = (key: string) => {
      const time = parsedTimeMs(key);
      if (time === null) return true;
      if (windowStartMs !== null && time < windowStartMs) return false;
      if (windowEndMs !== null && time >= windowEndMs) return false;
      return true;
    };

    const labels = new Map<string, string>();
    const actualPhysical = new Map<string, PowerBucket>();
    const declaredStrategy = new Map<string, PowerBucket>();
    const theoreticalLimit = new Map<string, PowerBucket>();
    const exAnteReview = new Map<string, PowerBucket>();
    const intradayDispatch = new Map<string, PowerBucket>();
    const prices = new Map<string, PriceBucket>();
    const keys = new Set<string>();

    for (const row of actualRows) {
      const key = floorToFifteenMinuteKey(timestampKey(targetDate, row.time));
      if (!inWindow(key)) continue;
      keys.add(key);
      addLabel(labels, key, labelFromKey(key));
      addPowerSample(actualPhysical, key, toFiniteNumber(row.bat), DEFAULT_ACTUAL_INTERVAL_HOURS);
      const declaredDischargePower = Math.max(0, toFiniteNumber(row.ep));
      const declaredChargePower = Math.min(0, toFiniteNumber(row.ep));
      addPowerSample(declaredStrategy, key, declaredDischargePower + declaredChargePower, DEFAULT_ACTUAL_INTERVAL_HOURS);
      addPriceSample(prices, key, toFiniteNumber(row.p));
    }

    for (const row of theoreticalRows) {
      const key = floorToFifteenMinuteKey(timestampKey(targetDate, row.period));
      if (!inWindow(key)) continue;
      keys.add(key);
      addLabel(labels, key, labelFromKey(key));
      addPowerSample(
        theoreticalLimit,
        key,
        toFiniteNumber(row.discharge_power) - toFiniteNumber(row.charge_power),
        DEFAULT_STRATEGY_INTERVAL_HOURS,
      );
      if (!prices.has(key)) addPriceSample(prices, key, toFiniteNumber(row.price));
    }

    const collectStrategyPath = (
      strategy: AgcStorageReviewStrategy | undefined,
      target: Map<string, PowerBucket>,
    ) => {
      const points = strategy?.calculation?.path_points ?? [];
      for (const point of points) {
        const key = floorToFifteenMinuteKey(String(point.timestamp));
        if (!inWindow(key)) continue;
        keys.add(key);
        addLabel(labels, key, labelFromKey(key));
        addPowerSample(
          target,
          key,
          toFiniteNumber(point.power_mw),
          toFiniteNumber(point.interval_hours, DEFAULT_STRATEGY_INTERVAL_HOURS),
        );
        if (!prices.has(key) && point.price_yuan_per_mwh !== null && point.price_yuan_per_mwh !== undefined) {
          addPriceSample(prices, key, toFiniteNumber(point.price_yuan_per_mwh));
        }
      }
    };

    collectStrategyPath(latestStrategy(reviewStrategies, 'ex_ante_review'), exAnteReview);
    collectStrategyPath(latestStrategy(reviewStrategies, 'intraday_dispatch'), intradayDispatch);

    const orderedKeys = buildFifteenMinuteAxis(Array.from(keys), windowStart, windowEnd);
    if (!orderedKeys.length) return {};
    const axisLabels = orderedKeys.map((key) => labels.get(key) ?? labelFromKey(key));
    const readPrice = () => readPriceSeries(prices, orderedKeys);

    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        valueFormatter: (value: any) => typeof value === 'number' ? format2(value) : value,
      },
      toolbox: { show: false },
      dataZoom: [
        { type: 'inside', throttle: 60 },
        { type: 'slider', height: 22, bottom: 18 },
      ],
      legend: {
        top: 0,
        data: [
          '实际物理收益',
          '实际申报策略',
          '事后理论上限',
          '事前复盘策略',
          '临场调度策略',
          '节点电价(元/MWh)',
        ],
      },
      grid: { left: 60, right: 82, top: 52, bottom: 72 },
      xAxis: {
        type: 'category',
        data: axisLabels,
        axisLabel: { rotate: 30, fontSize: 10 },
      },
      yAxis: [
        {
          type: 'value',
          name: '充放电功率 (MW)',
          position: 'left',
        },
        {
          type: 'value',
          name: '节点电价 (元/MWh)',
          position: 'right',
          axisLine: { show: true },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: '实际物理收益',
          type: 'bar',
          data: readPowerSeries(actualPhysical, orderedKeys),
          itemStyle: { color: 'rgba(114,46,209,0.34)' },
          yAxisIndex: 0,
        },
        {
          name: '实际申报策略',
          type: 'line',
          data: readPowerSeries(declaredStrategy, orderedKeys),
          step: 'end',
          lineStyle: { color: '#d4380d', width: 2 },
          itemStyle: { color: '#d4380d' },
          showSymbol: false,
          yAxisIndex: 0,
        },
        {
          name: '事后理论上限',
          type: 'line',
          data: readPowerSeries(theoreticalLimit, orderedKeys),
          step: 'end',
          lineStyle: { color: '#1677ff', width: 2.4 },
          itemStyle: { color: '#1677ff' },
          showSymbol: false,
          yAxisIndex: 0,
        },
        {
          name: '事前复盘策略',
          type: 'line',
          data: readPowerSeries(exAnteReview, orderedKeys),
          step: 'end',
          lineStyle: { color: '#08979c', width: 2.4 },
          itemStyle: { color: '#08979c' },
          showSymbol: false,
          yAxisIndex: 0,
        },
        {
          name: '临场调度策略',
          type: 'line',
          data: readPowerSeries(intradayDispatch, orderedKeys),
          step: 'end',
          lineStyle: { color: '#faad14', width: 2.4 },
          itemStyle: { color: '#faad14' },
          showSymbol: false,
          yAxisIndex: 0,
        },
        {
          name: '节点电价(元/MWh)',
          type: 'line',
          data: readPrice(),
          step: 'end',
          lineStyle: { color: '#52c41a', type: 'dashed' as const, width: 1.8 },
          itemStyle: { color: '#52c41a' },
          showSymbol: false,
          yAxisIndex: 1,
        },
      ],
    };
  }, [actualRows, theoreticalRows, reviewStrategies, targetDate, windowStart, windowEnd]);
}

function useExecOption(rows: AGCStorageDetailRow[]): EChartsOption {
  const times = useMemo(() => rows.map((r) => r.time), [rows]);
  return useMemo(() => {
    if (!rows.length) return {};
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        valueFormatter: (value: any) => typeof value === 'number' ? format2(value) : value,
      },
      toolbox: { show: false },
      dataZoom: { show: false },
      legend: { data: ['光伏可用出力', '光伏实际出力', 'AGC总目标值', '并网点实际总功率', '超短期功率预测', '储能出力曲线'] },
      grid: { left: 60, right: 40, top: 40, bottom: 60 },
      xAxis: {
        type: 'category',
        data: times,
        axisLabel: { rotate: 30, fontSize: 10 },
      },
      yAxis: {
        type: 'value',
        name: '功率 (MW)',
      },
      series: [
        {
          name: '光伏可用出力',
          type: 'line',
          data: rows.map((r) => r.pv_avail),
          lineStyle: { color: '#13c2c2', type: 'dashed' },
          itemStyle: { color: '#13c2c2' },
          showSymbol: false,
        },
        {
          name: '光伏实际出力',
          type: 'line',
          data: rows.map((r) => r.pv_act),
          areaStyle: { color: 'rgba(24,144,255,0.15)' },
          lineStyle: { color: '#1890ff' },
          itemStyle: { color: '#1890ff' },
          showSymbol: false,
        },
        {
          name: 'AGC总目标值',
          type: 'line',
          data: rows.map((r) => r.agc_target),
          lineStyle: { color: '#faad14', width: 2 },
          itemStyle: { color: '#faad14' },
          showSymbol: false,
        },
        {
          name: '并网点实际总功率',
          type: 'line',
          data: rows.map((r) => r.agc_act),
          lineStyle: { color: '#f5222d', width: 1.5 },
          itemStyle: { color: '#f5222d' },
          showSymbol: false,
        },
        {
          name: '超短期功率预测',
          type: 'line',
          data: rows.map((r) => r.ust),
          lineStyle: { color: '#52c41a', type: 'dotted' },
          itemStyle: { color: '#52c41a' },
          showSymbol: false,
        },
        {
          name: '储能出力曲线',
          type: 'bar',
          data: rows.map((r) => r.bat),
          itemStyle: { color: 'rgba(114,46,209,0.4)' },
        },
      ],
    };
  }, [rows, times]);
}

function usePriceOption(rows: AGCStorageDetailRow[]): EChartsOption {
  const times = useMemo(() => rows.map((r) => r.time), [rows]);
  return useMemo(() => {
    if (!rows.length) return {};
    return {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'cross' },
        valueFormatter: (value: any) => typeof value === 'number' ? format2(value) : value,
      },
      toolbox: { show: false },
      dataZoom: { show: false },
      legend: { data: ['储能出力曲线', '日前申报配储功率', '实时电价(元/MWh)'] },
      grid: { left: 60, right: 80, top: 40, bottom: 60 },
      xAxis: {
        type: 'category',
        data: times,
        axisLabel: { rotate: 30, fontSize: 10 },
      },
      yAxis: [
        {
          type: 'value',
          name: '功率 (MW)',
          position: 'left',
        },
        {
          type: 'value',
          name: '电价 (元/MWh)',
          position: 'right',
          axisLine: { show: true },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: '储能出力曲线',
          type: 'bar',
          data: rows.map((r) => r.bat),
          itemStyle: { color: 'rgba(114,46,209,0.4)' },
          yAxisIndex: 0,
        },
        {
          name: '日前申报配储功率',
          type: 'line',
          data: rows.map((r) => r.ep),
          lineStyle: { color: '#f5222d', width: 2.5 },
          itemStyle: { color: '#f5222d' },
          showSymbol: false,
          yAxisIndex: 0,
        },
        {
          name: '实时电价(元/MWh)',
          type: 'line',
          data: rows.map((r) => r.p),
          lineStyle: { color: '#52c41a', type: 'dashed' as const },
          itemStyle: { color: '#52c41a' },
          showSymbol: false,
          yAxisIndex: 1,
        },
      ],
    };
  }, [rows, times]);
}

export const StorageExecChart: React.FC<SingleChartProps> = ({
  rows,
  loading,
  error,
  onRetry,
  height = 450,
  title = '图表一：物理执行与AGC复盘',
}) => {
  const option = useExecOption(rows);
  return (
    <ChartCard
      title={title}
      option={option}
      loading={loading}
      error={error}
      onRetry={onRetry}
      height={height}
    />
  );
};

export const StoragePriceChart: React.FC<SingleChartProps> = ({
  rows,
  loading,
  error,
  onRetry,
  height = 450,
  title = '图表二：策略关联与电价对账',
}) => {
  const option = usePriceOption(rows);
  return (
    <ChartCard
      title={title}
      option={option}
      loading={loading}
      error={error}
      onRetry={onRetry}
      height={height}
    />
  );
};

export const StorageStrategyCompareChart: React.FC<StrategyCompareChartProps> = ({
  actualRows,
  theoreticalRows,
  loading,
  error,
  onRetry,
  height = 450,
  title = '图表：实际 / 申报 / 理论充放电与电价',
}) => {
  const option = useStrategyCompareOption(actualRows, theoreticalRows);
  return (
    <ChartCard
      title={title}
      option={option}
      loading={loading}
      error={error}
      onRetry={onRetry}
      height={height}
    />
  );
};

export const MultiPerspectiveReviewChart: React.FC<MultiPerspectiveReviewChartProps> = ({
  actualRows,
  theoreticalRows,
  reviewStrategies,
  targetDate,
  windowStart,
  windowEnd,
  loading,
  error,
  onRetry,
  height = 480,
  title = '节点电价与多口径充放电路径',
}) => {
  const option = useMultiPerspectiveReviewOption(actualRows, theoreticalRows, reviewStrategies, targetDate, windowStart, windowEnd);
  return (
    <ChartCard
      title={title}
      option={option}
      loading={loading}
      error={error}
      onRetry={onRetry}
      height={height}
    />
  );
};

const StorageDetailCharts: React.FC<StorageDetailChartsProps> = ({
  rows,
  loading = false,
  error = null,
  onRetry,
}) => {
  return (
    <>
      <StorageExecChart rows={rows} loading={loading} error={error} onRetry={onRetry} />
      <div style={{ marginTop: 16 }} />
      <StoragePriceChart rows={rows} loading={loading} error={error} onRetry={onRetry} />
    </>
  );
};

export default StorageDetailCharts;
